/**
 * <shipping-date> – termin wysyłki = dzisiaj + N dni roboczych.
 * Liczone w przeglądarce w strefie Europe/Warsaw; pomija soboty, niedziele
 * i polskie święta państwowe (w tym ruchome: Wielkanoc, Zielone Świątki, Boże Ciało).
 */
const WEEKDAYS = ['w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę', 'w czwartek', 'w piątek', 'w sobotę'];
const MONTHS = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
];
const DAY_MS = 86400000;

/** Niedziela Wielkanocna (algorytm Meeusa/Jonesa/Butchera) – zwraca [miesiąc 1-12, dzień]. */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return [month, day];
}

const holidayCache = new Map();

/** Zbiór świąt państwowych w danym roku jako klucze „RRRR-M-D”. */
function holidaysFor(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);

  const keys = new Set();
  const add = (month, day) => keys.add(`${year}-${month}-${day}`);

  [[1, 1], [1, 6], [5, 1], [5, 3], [8, 15], [11, 1], [11, 11], [12, 25], [12, 26]].forEach(([m, d]) => add(m, d));
  // Wigilia jest dniem wolnym od 2025 r.
  if (year >= 2025) add(12, 24);

  const [em, ed] = easterSunday(year);
  const easter = Date.UTC(year, em - 1, ed);
  [0, 1, 49, 60].forEach((offset) => {
    const date = new Date(easter + offset * DAY_MS);
    add(date.getUTCMonth() + 1, date.getUTCDate());
  });

  holidayCache.set(year, keys);
  return keys;
}

function isBusinessDay(date, skipHolidays) {
  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  if (!skipHolidays) return true;
  return !holidaysFor(date.getUTCFullYear()).has(`${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`);
}

/** Dzisiejsza data w podanej strefie jako Date UTC o północy. */
function todayIn(timeZone) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date())
      .split('-')
      .map(Number);
  } catch (error) {
    const now = new Date();
    parts = [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  }
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}

export function shippingDateLabel({ days = 2, skipHolidays = true, timeZone = 'Europe/Warsaw' } = {}) {
  let date = todayIn(timeZone);
  let added = 0;
  let guard = 0;

  while (added < days && guard < 60) {
    guard += 1;
    date = new Date(date.getTime() + DAY_MS);
    if (isBusinessDay(date, skipHolidays)) added += 1;
  }

  guard = 0;
  while (!isBusinessDay(date, skipHolidays) && guard < 15) {
    guard += 1;
    date = new Date(date.getTime() + DAY_MS);
  }

  return `${WEEKDAYS[date.getUTCDay()]}, ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

class ShippingDate extends HTMLElement {
  connectedCallback() {
    this.update();
  }

  update() {
    const target = this.querySelector('[data-ship-date-text]');
    if (!target) return;

    const days = Number.parseInt(this.dataset.days ?? '2', 10);
    const label = shippingDateLabel({
      days: Number.isNaN(days) ? 2 : days,
      skipHolidays: this.dataset.holidays !== 'false',
      timeZone: this.dataset.timezone || 'Europe/Warsaw',
    });

    const template = this.dataset.template || '';
    const pieces = template.split('{data}');
    const nodes = [];

    pieces.forEach((piece, index) => {
      if (piece) nodes.push(document.createTextNode(piece));
      if (index < pieces.length - 1) {
        const strong = document.createElement('strong');
        strong.textContent = label;
        nodes.push(strong);
      }
    });

    if (nodes.length) target.replaceChildren(...nodes);
  }
}

if (!customElements.get('shipping-date')) {
  customElements.define('shipping-date', ShippingDate);
}
